import moment from "moment";

export type TransactionStatusKind = "previsto" | "realizado" | "vencido";

const BACKEND_STATUS_MAP: Record<string, TransactionStatusKind> = {
  REALIZED: "realizado",
  FORECASTED: "previsto",
  OVERDUE: "vencido",
};

const isBeforeToday = (date?: string | null): boolean =>
  !!date && moment(date, moment.ISO_8601, true).isValid() && moment(date).isBefore(moment(), "day");

const deriveFromTransactionDate = (transactionDate?: string): TransactionStatusKind =>
  transactionDate && moment(transactionDate).isAfter(moment(), "day") ? "previsto" : "realizado";

const OVERDUE_FORM_STATUSES = new Set(["vencido", "ematraso"]);

const DUE_DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY"];

export const isOverdueFormStatus = (status?: string | null): boolean =>
  OVERDUE_FORM_STATUSES.has(status?.trim().toLowerCase() ?? "");

export const hasInvalidOverdueDueDate = (status?: string | null, dueDate?: string | null): boolean => {
  if (!isOverdueFormStatus(status)) return false;

  const due = moment(dueDate?.split("T")[0] ?? "", DUE_DATE_FORMATS, true);

  return due.isValid() && !due.isBefore(moment(), "day");
};

export const deriveStatusFromDueDate = (dueDate?: string | null): TransactionStatusKind | "" => {
  if (!dueDate) return "";

  const due = moment(dueDate.split("T")[0], "YYYY-MM-DD", true);
  if (!due.isValid()) return "";

  if (due.isSame(moment(), "day")) return "realizado";
  if (due.isAfter(moment(), "day")) return "previsto";

  return "vencido";
};

export const resolveTransactionStatus = (
  backendStatus?: string | null,
  dueDate?: string | null,
  transactionDate?: string
): TransactionStatusKind => {
  const normalized = backendStatus?.trim().toUpperCase();
  const mapped = normalized ? BACKEND_STATUS_MAP[normalized] : undefined;

  if (!mapped) return deriveFromTransactionDate(transactionDate);
  if (mapped === "realizado" || mapped === "vencido") return mapped;

  return isBeforeToday(dueDate) ? "vencido" : "previsto";
};

export const getStatusLabelKeyFromKind = (kind: TransactionStatusKind, module?: string | null): string => {
  if (String(module ?? "").toUpperCase() === "RECEITA") {
    return kind === "vencido" ? "receipt.status.emAtraso" : `receipt.status.${kind}`;
  }

  return `expense.status.${kind}`;
};

export const getTransactionStatusLabelKey = (module?: string | null, backendStatus?: string | null): string =>
  getStatusLabelKeyFromKind(resolveTransactionStatus(backendStatus), module);

export const getAuditStatusLabelKey = (module?: string | null, backendStatus?: string | null): string | null => {
  const normalized = backendStatus?.trim().toUpperCase();
  const kind = normalized ? BACKEND_STATUS_MAP[normalized] : undefined;

  return kind ? getTransactionStatusLabelKey(module, backendStatus) : null;
};
